import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import useIsMainUserOfInstitution from 'utils/hooks/auth/useIsMainUserOfInstitution';
import useSearchSubmissions from 'utils/hooks/submissions/useSearchSubmissions';
import useSelectedInstitution from 'utils/hooks/institutions/useSelectedInstitution';
import useInterval from 'utils/hooks/useInterval';
import useRedirect from 'utils/hooks/useRedirect';
import { toggleSubmissionFilters } from 'store/entities/ui';
import { TWO_MINUTES } from 'utils/constants/timeConstants';

import List from './List';
import Filters from './Filters';
import UploadListFile from './List/UploadListFile';
import BlackBoxUpload from 'views/BlackboxSubmission/BlackBoxUpload';

import { Section } from 'components/UIComponents/Layout';
import { Title } from 'components/UIComponents/Typography';
import Accordion from 'components/UIComponents/Accordion';
import Button from 'components/UIComponents/Button';
import LoadingSpinner from 'components/UIComponents/LoadingSpinner';

const Submissions = () => {
  const { t } = useTranslation();
  const redirect = useRedirect();
  const dispatch = useDispatch();
  const { institutionId, institutionName } = useSelectedInstitution();
  const isMainUserOfInstitution = useIsMainUserOfInstitution();

  const {
    reporter,
    institution,
    status,
    startDate,
    endDate,
    type,
    showAllProblematic,
    submissionsListPageSize,
    submissionsListCurrentPage,
    submissionsListLoading,
  } = useSelector(state => state.submissions);
  const { submissionFiltersOpen } = useSelector(state => state.ui);

  const doSearch = useSearchSubmissions();

  useEffect(() => {
    doSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    institution,
    reporter,
    status,
    startDate,
    endDate,
    type,
    submissionsListPageSize,
    submissionsListCurrentPage,
    institutionId,
    showAllProblematic,
  ]);

  useInterval(() => {
    doSearch();
  }, TWO_MINUTES);

  return (
    <div>
      <LoadingSpinner loading={submissionsListLoading} position="global" />
      <Section size="medium" className="pt-1 pb-1 dotted">
        <Title
          title={
            institutionId ? (
              <span className="flex-center-y flex-wrap">
                <span className="mr-2 bg-white">{institutionName}</span>
                {isMainUserOfInstitution && (
                  <Button
                    color="primary"
                    className="ml-5"
                    onClick={() => redirect(`/${institutionId}/reporters`)}
                  >
                    {t('datagate.external.users.title')}
                  </Button>
                )}
              </span>
            ) : (
              <span>
                <span className="bg-white p-2">
                  {t('datagate.external.submissions.title')}
                </span>
              </span>
            )
          }
        />
      </Section>
      <Section size="medium" className="pt-1 pb-5 dotted">
        <Accordion
          label={t('datagate.external.submissions.title.filters')}
          open={submissionFiltersOpen}
          onClick={() =>
            dispatch(toggleSubmissionFilters(!submissionFiltersOpen))
          }
        >
          <Filters />
        </Accordion>
      </Section>
      <Section size="medium" className="pt-0 mt-4">
        <List />
      </Section>
      <UploadListFile />
      <BlackBoxUpload />
    </div>
  );
};

export default Submissions;
